import { createContext, useCallback, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import { useWebSocketEvent } from './useWebSocket'
import type { WebSocketHookReturn } from './useWebSocket'

export interface SanityEvt {
  rule_name?: string
  decision?: string
  symbol?: string
  reason_detail?: string
  timestamp?: string
}

// The rolling buffer is 100 events deep (10 pages of the feed).
const BUFFER_SIZE = 100

const SanityGateBufferContext = createContext<SanityEvt[] | null>(null)

/**
 * Session-long owner of the sanity-gate event buffer. Mounted once by
 * AppShell so lazily-mounted consumers (drawer panels, module faces) read
 * events accumulated since page load rather than starting empty.
 */
export function SanityGateBufferProvider({
  ws,
  children,
}: {
  ws: WebSocketHookReturn
  children: ReactNode
}) {
  const [events, setEvents] = useState<SanityEvt[]>([])
  // Stable callback so useWebSocketEvent subscribes once per mount, not per render.
  const onEvent = useCallback((e: SanityEvt) => {
    setEvents((prev) => [e, ...prev].slice(0, BUFFER_SIZE))
  }, [])
  useWebSocketEvent<SanityEvt>(ws, 'sanity_gate_event', onEvent)
  return (
    <SanityGateBufferContext.Provider value={events}>
      {children}
    </SanityGateBufferContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSanityGateBuffer(): SanityEvt[] {
  const ctx = useContext(SanityGateBufferContext)
  if (!ctx) throw new Error('useSanityGateBuffer must be used inside <SanityGateBufferProvider>')
  return ctx
}
